import React, { useState, useEffect } from 'react';
import { X, Percent, DollarSign, Check, AlertCircle, RefreshCw, Search, Tag } from 'lucide-react';
import { useDiscounts, useAssignDiscountToStudents } from '../../hooks/useDiscounts';
import { getAcademicYears } from '@/services/academicManagementService';
import { formatCurrency } from '@/services/tuitionService';
import type { DiscountConfig } from '@/types/tuition.types';

interface Props {
  studentId: string;
  studentName: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface FilterOption {
  value: string;
  label: string;
  isCurrent?: boolean;
}

// Type metadata for display
const DISCOUNT_TYPE_META: Record<string, { label: string; icon: string; color: string }> = {
  SIBLING: { label: 'Fratrie', icon: '👨‍👩‍👧‍👦', color: 'bg-blue-100 text-blue-700' },
  MERIT: { label: 'Mérite', icon: '🏆', color: 'bg-yellow-100 text-yellow-700' },
  PARTNER: { label: 'Partenaire', icon: '🤝', color: 'bg-purple-100 text-purple-700' },
  SOCIAL: { label: 'Social', icon: '❤️', color: 'bg-red-100 text-red-700' },
  EARLY_PAYMENT: { label: 'Anticipé', icon: '⏰', color: 'bg-green-100 text-green-700' },
  STAFF_CHILD: { label: 'Personnel', icon: '👨‍💼', color: 'bg-indigo-100 text-indigo-700' },
  AMBASSADOR: { label: 'Ambassadeur', icon: '🌟', color: 'bg-orange-100 text-orange-700' },
  OTHER: { label: 'Autre', icon: '📋', color: 'bg-gray-100 text-gray-700' },
};

const AssignDiscountModal: React.FC<Props> = ({ studentId, studentName, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDiscount, setSelectedDiscount] = useState<string | null>(null);
  const [academicYears, setAcademicYears] = useState<FilterOption[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [isLoadingYears, setIsLoadingYears] = useState(true);

  // Fetch active discounts
  const { data: discountsData, isLoading: isLoadingDiscounts } = useDiscounts({ includeArchived: false });
  const assignMutation = useAssignDiscountToStudents();

  const discounts = discountsData?.data?.filter((d: DiscountConfig) => d.isActive) || [];

  // Filter discounts by search
  const filteredDiscounts = discounts.filter((discount: DiscountConfig) =>
    discount.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    discount.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load academic years
  useEffect(() => {
    const loadYears = async () => {
      try {
        const years = await getAcademicYears();
        setAcademicYears(years);
        const currentYear = years.find((y: FilterOption) => y.isCurrent);
        if (currentYear) {
          setSelectedAcademicYear(currentYear.value);
        }
      } catch (err) {
        console.error('Error loading academic years:', err);
      } finally {
        setIsLoadingYears(false);
      }
    };
    loadYears();
  }, []);

  const handleAssign = async () => {
    if (!selectedDiscount) {
      setError('Veuillez sélectionner une remise');
      return;
    }

    if (!selectedAcademicYear) {
      setError('Veuillez sélectionner une année académique');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      await assignMutation.mutateAsync({
        discountConfigId: selectedDiscount,
        studentIds: [studentId],
        academicYearId: selectedAcademicYear,
      });

      const discount = discounts.find((d: DiscountConfig) => d.id === selectedDiscount);
      setSuccess(`Remise "${discount?.name}" affectée à ${studentName}`);
      setSelectedDiscount(null);

      // Call onSuccess after a short delay to show the success message
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'affectation de la remise');
    }
  };

  const getDiscountValue = (discount: DiscountConfig) => {
    if (discount.percentageValue !== null) {
      return `${discount.percentageValue}%`;
    }
    return formatCurrency(discount.fixedAmount || 0);
  };

  const isLoading = isLoadingYears || isLoadingDiscounts;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Affecter une remise</h2>
            <p className="text-sm text-gray-500">{studentName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
              <Check size={16} />
              {success}
            </div>
          )}

          {/* Academic Year Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Année académique
            </label>
            <select
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isLoadingYears}
            >
              <option value="">Sélectionner une année</option>
              {academicYears.map((year) => (
                <option key={year.value} value={year.value}>
                  {year.label} {year.isCurrent && '(Actuelle)'}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une remise..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Discounts List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : filteredDiscounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="mx-auto w-10 h-10 mb-2 text-gray-400" />
              <p>Aucune remise disponible</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDiscounts.map((discount: DiscountConfig) => {
                const meta = DISCOUNT_TYPE_META[discount.type] || DISCOUNT_TYPE_META.OTHER;
                const isSelected = selectedDiscount === discount.id;

                return (
                  <button
                    key={discount.id}
                    onClick={() => setSelectedDiscount(isSelected ? null : discount.id)}
                    className={`w-full p-3 border rounded-lg flex items-center gap-3 transition-colors text-left ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${meta.color}`}
                    >
                      {meta.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{discount.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className={`px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                        {discount.code && <span>({discount.code})</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 text-right">
                        {discount.percentageValue !== null ? (
                          <Percent size={14} className="text-primary-500" />
                        ) : (
                          <DollarSign size={14} className="text-primary-500" />
                        )}
                        <span className="font-semibold text-primary-700">
                          {getDiscountValue(discount)}
                        </span>
                      </div>
                      {isSelected && (
                        <Check size={18} className="text-primary-600" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Info about requires approval */}
          {selectedDiscount && (() => {
            const discount = discounts.find((d: DiscountConfig) => d.id === selectedDiscount);
            if (discount?.requiresApproval) {
              return (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>
                    Cette remise nécessite une approbation. Elle sera en attente jusqu'à validation par un responsable.
                  </span>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedDiscount || !selectedAcademicYear || assignMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {assignMutation.isPending ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Affectation...
              </>
            ) : (
              <>
                <Check size={16} />
                Affecter la remise
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignDiscountModal;
